import React, { useRef } from 'react';
import { Input } from '../ui/input';
import { fmtCurrency, fmtNumber } from '../../lib/financeMath';

// Bank-style numeric inputs. The buyer sees their own language's separators
// while typing — EN "34,000" / "$48,000", FR "34 000" / "48 000 $" — and the
// parent always receives PLAIN DIGITS ("34000"), so submit/compare logic never
// has to parse a formatted string.
//
// Formatting comes from lib/financeMath.js (the single source of truth for
// locale-aware money and numbers), NOT from a hardcoded en-US toLocaleString.
export const digitsOnly = (v) => String(v ?? '').replace(/[^\d]/g, '');

export const formatMoney = (v) => {
    const d = digitsOnly(v);
    return d ? fmtCurrency(Number(d)) : '';
};

export const formatCount = (v) => {
    const d = digitsOnly(v);
    return d ? fmtNumber(Number(d)) : '';
};

/**
 * Re-formatting on every keystroke moves the caret to the end unless it is put
 * back deliberately. So we count the DIGITS left of the caret before the edit
 * and restore the caret after that same digit in the reformatted string —
 * typing, backspacing and editing mid-number all stay where the finger is.
 */
const FormattedNumberInput = ({ value, onChange, format, inputMode = 'numeric', ...rest }) => {
    const ref = useRef(null);

    const handleChange = (e) => {
        const el = e.target;
        const caret = el.selectionStart ?? el.value.length;
        const digitsBeforeCaret = digitsOnly(el.value.slice(0, caret)).length;
        const digits = digitsOnly(el.value);
        onChange(digits);
        const next = format(digits);
        requestAnimationFrame(() => {
            const node = ref.current;
            if (!node) return;
            let i = 0;
            let seen = 0;
            while (i < next.length && seen < digitsBeforeCaret) {
                if (/\d/.test(next[i])) seen += 1;
                i += 1;
            }
            try { node.setSelectionRange(i, i); } catch { /* unsupported input type */ }
        });
    };

    return (
        <Input {...rest} ref={ref} inputMode={inputMode} autoComplete="off"
               value={format(value)} onChange={handleChange} />
    );
};

export const CurrencyInput = (props) => <FormattedNumberInput {...props} format={formatMoney} />;

/** Mileage / odometer / any plain count. No currency symbol. */
export const NumberInput = (props) => <FormattedNumberInput {...props} format={formatCount} />;

export default CurrencyInput;
